/**
 * Cart Drawer Gift Wrap functionality with re-render handling.
 * Uses event delegation and listens for cart drawer updates.
 */

import { definePackagingBlock } from '../gift-wrap/src/packaging/cart/PackagingBlockElement';
import { initGiftOptionsAvailability } from '../gift-wrap/src/packaging/cart/giftOptionsAvailability';

// ALBAU-884: the cart drawer's packaging block and "Add Gift Options" availability (packaging_flow_enabled).
definePackagingBlock();
initGiftOptionsAvailability();

// Global flag to track if event listener is attached
let isEventListenerAttached = false;

// TypeScript declarations for window properties
/**
 * @typedef {Object} GiftWrapData
 * @property {boolean} enabled
 * @property {Object} productData
 * @property {Object} themeSettings
 */

/**
 * Handle gift wrap button clicks using event delegation.
 * @param {Event} event - The click event.
 */
function handleGiftWrapClick(event) {
  const target = event.target;
  if (!target) return;

  // @ts-expect-error - closest method exists on Element but not on EventTarget
  const button = target.closest ? target.closest('.js-gw-add-luxury-gift-wrap') : null;

  if (button && button.classList.contains('js-gw-add-luxury-gift-wrap')) {
    event.preventDefault();
    event.stopPropagation();

    // Get button data.
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    const productPrice = button.dataset.productPrice;
    const productId = button.dataset.productId;
    const cartItemKey = button.dataset.cartItemKey;
    const cartItemIndex = button.dataset.cartItemIndex;
    const vendorRoutingMatch = button.dataset.vendorRoutingMatch || null;

    if (!variantId) {
      console.warn('No variant ID found on gift wrap button.');
      return;
    }

    // Check if gift wrap system is enabled.
    if (!window.giftWrapData?.enabled) {
      console.warn('Gift wrap system is not enabled.');
      return;
    }

    // Create product data in the format expected by GiftWrapContext.
    const product = {
      id: productId || variantId,
      title: productTitle || 'Unknown Product',
      price: productPrice ? parseInt(productPrice, 10) : 0,
      vendor: vendorRoutingMatch || null
    };

    // Create cart data in the format expected by GiftWrapEventListener.
    const cartData = {
      variant_id: parseInt(variantId, 10),
      key: cartItemKey || '',
      product_title: productTitle || 'Unknown Product',
      price: productPrice ? parseInt(productPrice, 10) : 0,
      product_id: productId ? parseInt(productId, 10) : null,
      line: parseInt(cartItemIndex || '1', 10)
    };

    // Dispatch GIFTWRAP_EVENT with cart data for pendingAddToCartData.
    const customEvent = new CustomEvent('GIFTWRAP_EVENT', {
      detail: {
        action: 'add_gift_wrap',
        variantId: variantId,
        productId: productId,
        product: product,
        cartData: cartData,
        allowOriginalFlow: false,
        source: 'cart-drawer-gift-wrap',
        vendor: vendorRoutingMatch
      },
      bubbles: true,
      cancelable: true
    });

    document.dispatchEvent(customEvent);
  }
}

/**
 * Set up event listeners for gift wrap functionality.
 */
function setupGiftWrapListeners() {
  if (isEventListenerAttached) {
    return;
  }

  // Use event delegation - attach to document so it works even after re-renders.
  document.addEventListener('click', handleGiftWrapClick);
  isEventListenerAttached = true;
}

/**
 * Handle cart drawer updates - re-setup listeners if needed.
 */
function handleCartDrawerUpdate() {
  // Check if buttons exist.
  const buttons = document.querySelectorAll('.js-gw-add-luxury-gift-wrap');

  if (buttons.length > 0 && !isEventListenerAttached) {
    setupGiftWrapListeners();
  }
}
// Initialize when DOM is ready.
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for other systems to initialize.
  setTimeout(() => {
    setupGiftWrapListeners();
    document.addEventListener('CART_DRAWER_UPDATED', handleCartDrawerUpdate);
  }, 100);
});

// Also set up listeners immediately if DOM is already ready.
if (document.readyState === 'loading') {
  // Document is still loading, wait for DOMContentLoaded.
} else {
  // Document is already ready, set up listeners immediately.
  setupGiftWrapListeners();
  document.addEventListener('CART_DRAWER_UPDATED', handleCartDrawerUpdate);
}
